//----------------------------------------------------------------------------------------------------------------------
// Collider Property Handler
//----------------------------------------------------------------------------------------------------------------------

import {
    AbstractMesh,
    Mesh,
    PhysicsAggregate,
    type PhysicsAggregateParameters,
    PhysicsShapeType,
    Quaternion,
    type TransformNode,
    Vector3,
} from '@babylonjs/core';

import type { LevelManager } from '../managers/level.ts';
import { SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

const logger = new SAGELogger('ColliderHandler');

/**
 * Computes collider bounds from a node's descendant hierarchy.
 *
 * This is needed when a parent TransformNode has no geometry of its own -- for example, when Blender's
 * GLB exporter splits a multi-material mesh into primitive children, giving us a bare TransformNode
 * parent with Mesh children. Without this, `PhysicsAggregate` would read the parent's raw bounding info
 * (a default unit cube) and produce a garbage collider.
 *
 * Returns `extents` (full box size) and `center` (offset from the node's origin in its local frame),
 * which is the format `PhysicsAggregate` expects in its options.
 */
function computeHierarchyBounds(node : TransformNode) : { extents : Vector3; center : Vector3 }
{
    const { min, max } = node.getHierarchyBoundingVectors(true);
    const extents = max.subtract(min);
    const worldCenter = min.add(max).scale(0.5);

    // Convert world center to an offset in the node's local frame.
    // For the common case (identity rotation) this is just worldCenter - absolutePosition.
    // If the parent has rotation, undo it so the center lives in the node's local space.
    const worldOffset = worldCenter.subtract(node.absolutePosition);
    const rotation = node.absoluteRotationQuaternion;
    const inverseRotation = Quaternion.Inverse(rotation);
    const center = new Vector3();
    worldOffset.rotateByQuaternionToRef(inverseRotation, center);

    return { extents, center };
}

/**
 * Creates a physics aggregate for a node with the given shape type.
 *
 * Works with both Mesh and TransformNode. For non-Mesh TransformNodes with child geometry
 * (multi-material mesh case), computes explicit extents/center from the hierarchy bounding box
 * to avoid `PhysicsAggregate` falling back to a default unit cube.
 */
function createPhysicsAggregate(node : TransformNode, shapeType : PhysicsShapeType, mass : number) : void
{
    const scene = node.getScene();
    const options : PhysicsAggregateParameters = { mass };

    // Non-Mesh TransformNodes have no geometry of their own; compute bounds from descendants.
    if(!(node instanceof Mesh) && (shapeType === PhysicsShapeType.BOX || shapeType === PhysicsShapeType.SPHERE))
    {
        const { extents, center } = computeHierarchyBounds(node);
        options.center = center;

        if(shapeType === PhysicsShapeType.BOX)
        {
            options.extents = extents;
        }
        else
        {
            // Sphere radius: use the largest half-extent so the sphere fully contains the geometry.
            options.radius = Math.max(extents.x, extents.y, extents.z) / 2;
        }
    }

    new PhysicsAggregate(node, shapeType, options, scene);
}

/**
 * Handles mesh collider setup, using a child mesh if marked with collider_mesh.
 */
function handleMeshCollider(mesh : Mesh, mass : number) : void
{
    const colliderChild = mesh.getChildMeshes().find(
        (child) => child.metadata?.collider_mesh === true
    );

    if(colliderChild instanceof Mesh)
    {
        colliderChild.isVisible = false;
        createPhysicsAggregate(colliderChild, PhysicsShapeType.MESH, mass);
    }
    else
    {
        createPhysicsAggregate(mesh, PhysicsShapeType.MESH, mass);
    }
}

/**
 * Register the collider property handler.
 *
 * Supports the following properties on scene nodes:
 * - `collider` (string): Collider type - "box", "sphere", "mesh", or "none"
 * - `collider_mesh` (boolean): On a child node, marks it as the collision source for parent's "mesh" collider
 * - `collider_mass` (number): Mass for the physics body. Defaults to 0 (static). Set > 0 for dynamic bodies.
 * - `collider_kinematic` (boolean): When true, sets `disablePreStep = false` so the physics body
 *   tracks mesh transform each frame. Required for animated colliders (doors, elevators, platforms).
 *
 * For "mesh" colliders, if a child node has `collider_mesh: true`, that child's geometry
 * is used for collision and the child is made invisible.
 *
 * @param levelManager
 */
export function registerColliderHandler(levelManager : LevelManager) : void
{
    levelManager.registerPropertyHandler('collider', (node, value, level, _gameEngine) =>
    {
        const colliderType = value as string;
        logger.debug(`Processing collider: ${ node.name } -> ${ colliderType } (isMesh: ${ node instanceof Mesh })`);

        if(!level.scene)
        {
            throw new Error('Scene not available for collider handler');
        }

        if(!(node instanceof Mesh) && colliderType === 'mesh')
        {
            logger.warn(`Skipping mesh collider for ${ node.name }: not a Mesh instance`);
            return;
        }

        const mass = (node.metadata?.collider_mass as number | undefined) ?? 0;

        switch (colliderType)
        {
            case 'box':
                createPhysicsAggregate(node, PhysicsShapeType.BOX, mass);
                break;

            case 'sphere':
                createPhysicsAggregate(node, PhysicsShapeType.SPHERE, mass);
                break;

            case 'mesh':
                handleMeshCollider(node as Mesh, mass);
                break;

            case 'none':
                if(node instanceof AbstractMesh)
                {
                    node.checkCollisions = false;
                }
                break;

            default:
                logger.warn(`Unknown collider type: ${ colliderType }`);
        }

        // Enable kinematic mode -- physics body tracks mesh transform each frame
        if(node.metadata?.collider_kinematic && node instanceof AbstractMesh)
        {
            if(node.physicsBody)
            {
                node.physicsBody.disablePreStep = false;
            }
        }
    });
}

//----------------------------------------------------------------------------------------------------------------------
